import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiDollarSign,
  FiDownload,
  FiFileText,
  FiSearch,
  FiSettings,
  FiClock,
} from 'react-icons/fi';
import api, { downloadAuthenticatedFile } from '../../config/api';
import LoadingButton from '../LoadingButton';
import Modal from '../Modal';
import ListSearchBar from '../ListSearchBar';
import { formatDateOnly } from '../../utils/formatTimestamp';
import formatMoney from '../../utils/formatMoney';
import { TAX_ID_TYPE_OPTIONS } from '../../utils/taxDocumentFields';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';
import { queryKeys, staleTimes } from '../../config/queryKeys';
import { toYmd } from '../../utils/listPagination';
import { appAlert, appConfirm } from '../../utils/appDialog';
import { StatusBadge, statusLabel as uiStatusLabel } from '../shared/Ui';

const PAGE_SIZE = 20;
const TAX_EXPORT_YEAR_SPAN = 6;

const money = formatMoney;

async function downloadPayoutInvoicePdf(docId, number) {
  if (!docId) return;
  try {
    await downloadAuthenticatedFile(
      `/services/sales/documents/${docId}/pdf`,
      `${number || 'payout-invoice'}.pdf`
    );
  } catch (e) {
    void appAlert(e?.response?.data?.error || e.message || 'Could not download PDF');
  }
}

/**
 * Default tax-export year: during January the prior year is the one being filed.
 */
function defaultTaxExportYear(now = new Date()) {
  return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
}

function statusLabel(status) {
  switch (status) {
    case 'ready':
      return 'Ready to invoice';
    case 'partial':
      return 'Partially invoiced';
    case 'invoiced':
      return 'Invoiced';
    default:
      return 'No payouts';
  }
}

/**
 * Finance pay-run workspace: pay cycles + consultant-scale payout invoicing.
 */
export default function ServicesFinancePanel({
  canManage,
  companies = [],
  initialTab = 'pay-run',
  onTabChange,
}) {
  const queryClient = useQueryClient();
  const [workspaceTab, setWorkspaceTab] = useState(
    initialTab === 'invoices' ? 'invoices' : 'pay-run'
  );
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceSearchDebounced, setInvoiceSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('payable'); // payable | all | ready | partial | invoiced
  const [page, setPage] = useState(0);
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedConsultantIds, setSelectedConsultantIds] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    cycle_type: 'days_28',
    anchor_date: '',
  });
  const [exportYear, setExportYear] = useState(defaultTaxExportYear);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setInvoiceSearchDebounced(invoiceSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [invoiceSearch]);

  useEffect(() => {
    setWorkspaceTab(initialTab === 'invoices' ? 'invoices' : 'pay-run');
  }, [initialTab]);

  const selectWorkspaceTab = (tab) => {
    setWorkspaceTab(tab);
    if (typeof onTabChange === 'function') onTabChange(tab);
  };

  const periodStartYmd = toYmd(periodStart);
  const periodEndYmd = toYmd(periodEnd);

  const { data: settingsData, isLoading: loadingSettings } = useQuery(
    queryKeys.financeSettings,
    () => api.get('/services/finance/settings').then((r) => r.data),
    {
      staleTime: staleTimes.reference,
      onSuccess: (data) => {
        if (!periodStart && data?.current_period) {
          setPeriodStart(toYmd(data.current_period.start));
          setPeriodEnd(toYmd(data.current_period.end));
        }
      },
    }
  );

  // Seed the cycle form when the modal opens so background refetches can't
  // overwrite what the user is typing.
  useEffect(() => {
    if (!showSettings) return;
    setSettingsForm({
      cycle_type: settingsData?.config?.cycle_type || 'days_28',
      anchor_date: toYmd(settingsData?.config?.anchor_date) || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per open
  }, [showSettings]);

  const periodReady = Boolean(periodStartYmd && periodEndYmd);

  const { data: payRun, isLoading: loadingPayRun } = useQuery(
    queryKeys.financePayRun({
      periodStart: periodStartYmd,
      periodEnd: periodEndYmd,
      q: searchDebounced,
    }),
    () =>
      api
        .get('/services/finance/pay-run', {
          params: {
            period_start: periodStartYmd,
            period_end: periodEndYmd,
            q: searchDebounced || undefined,
          },
        })
        .then((r) => r.data),
    { enabled: periodReady, staleTime: staleTimes.lists }
  );

  const { data: consultantEntries = [], isLoading: loadingEntries } = useQuery(
    queryKeys.financeLedger({
      periodStart: periodStartYmd,
      periodEnd: periodEndYmd,
      consultantId: selectedConsultantId,
    }),
    () =>
      api
        .get('/services/finance/ledger', {
          params: {
            period_start: periodStartYmd,
            period_end: periodEndYmd,
            consultant_id: selectedConsultantId,
            payouts: 1,
            limit: 500,
          },
        })
        .then((r) => r.data),
    { enabled: periodReady && Boolean(selectedConsultantId), staleTime: staleTimes.lists }
  );

  const { data: payoutInvoicesData, isLoading: loadingPayoutInvoices } = useQuery(
    queryKeys.financePayoutInvoices({ q: invoiceSearchDebounced }),
    () =>
      api
        .get('/services/finance/payout-invoices', {
          params: {
            q: invoiceSearchDebounced || undefined,
            limit: 100,
          },
        })
        .then((r) => r.data),
    {
      enabled: workspaceTab === 'invoices',
      staleTime: staleTimes.lists,
    }
  );
  const payoutInvoices = payoutInvoicesData?.invoices || [];
  const {
    pageItems: pagedPayoutInvoices,
    pagination: invoicePagination,
    setPage: setInvoicePage,
  } = useClientPager(payoutInvoices, { resetKey: invoiceSearchDebounced || '' });

  const saveSettingsMutation = useMutation(
    (payload) => api.put('/services/finance/settings', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(queryKeys.financeSettings);
        queryClient.invalidateQueries('service-finance-pay-run');
        const period = res.data?.current_period;
        if (period) {
          setPeriodStart(toYmd(period.start));
          setPeriodEnd(toYmd(period.end));
        }
        setShowSettings(false);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const financeUpdateMutation = useMutation(
    ({ id, status }) => api.put(`/services/finance/ledger/${id}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('service-finance-pay-run');
        queryClient.invalidateQueries(['service-finance-ledger']);
        queryClient.invalidateQueries('service-finance-summary');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const generateInvoiceMutation = useMutation(
    async ({ entryIds, consultantGroups }) => {
      const groups = consultantGroups || [{ entry_ids: entryIds }];
      const results = [];
      const failures = [];
      // Posts are sequential; keep going so one bad consultant does not hide
      // the invoices that were created before it.
      for (const group of groups) {
        try {
          const res = await api.post('/services/finance/ledger/generate-invoice', {
            entry_ids: group.entry_ids,
            company_id: companies[0]?.id || null,
            period_start: periodStartYmd,
            period_end: periodEndYmd,
            generate_pdf: true,
          });
          results.push(res.data);
        } catch (e) {
          failures.push({
            label: group.label || `${group.entry_ids.length} payout(s)`,
            message: e.response?.data?.error || e.message,
          });
        }
      }
      if (!results.length && failures.length) {
        throw new Error(failures.map((f) => `${f.label}: ${f.message}`).join('\n'));
      }
      return { results, failures };
    },
    {
      onSuccess: async ({ results, failures }) => {
        queryClient.invalidateQueries('service-finance-pay-run');
        queryClient.invalidateQueries(['service-finance-ledger']);
        queryClient.invalidateQueries('service-finance-summary');
        queryClient.invalidateQueries(['service-finance-payout-invoices']);
        setSelectedEntryIds([]);
        setSelectedConsultantIds([]);
        for (const data of results) {
          const doc = data?.document;
          if (doc?.id && data?.pdf_generated) {
            try {
              await downloadAuthenticatedFile(
                `/services/sales/documents/${doc.id}/pdf`,
                `${doc.number || 'invoice'}.pdf`
              );
            } catch {
              /* download optional */
            }
          }
        }
        const summary =
          results.length === 1
            ? `Invoice ${results[0]?.document?.number || ''} created for ${
                results[0]?.consultant_name || 'consultant'
              } (${results[0]?.linked_entry_ids?.length || 0} payouts in period).`
            : `Created ${results.length} period invoices.`;
        void appAlert(
          failures.length
            ? `${summary}\n\n${failures.length} failed:\n${failures
                .map((f) => `• ${f.label}: ${f.message}`)
                .join('\n')}`
            : summary
        );
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const cycleTypes = settingsData?.cycle_types || [];
  const consultants = useMemo(() => payRun?.consultants || [], [payRun?.consultants]);

  const filteredConsultants = useMemo(() => {
    if (statusFilter === 'all') return consultants;
    if (statusFilter === 'payable') {
      return consultants.filter(
        (c) => c.invoice_status === 'ready' || c.invoice_status === 'partial'
      );
    }
    return consultants.filter((c) => c.invoice_status === statusFilter);
  }, [consultants, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredConsultants.length / PAGE_SIZE));
  const pageRows = filteredConsultants.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [searchDebounced, statusFilter, periodStart, periodEnd]);

  useEffect(() => {
    if (
      selectedConsultantId &&
      !filteredConsultants.some((c) => String(c.consultant_id) === String(selectedConsultantId))
    ) {
      setSelectedConsultantId(null);
      setSelectedEntryIds([]);
    }
  }, [filteredConsultants, selectedConsultantId]);

  const selectedConsultant = consultants.find(
    (c) => String(c.consultant_id) === String(selectedConsultantId)
  );

  const uninvoicedEntries = useMemo(
    () => (consultantEntries || []).filter((r) => !r.sales_document_id),
    [consultantEntries]
  );

  /** Period totals are only meaningful when every consultant bills one currency. */
  const payRunCurrencies = useMemo(
    () => [
      ...new Set(
        consultants.map((c) => String(c.currency || '').trim().toUpperCase()).filter(Boolean)
      ),
    ],
    [consultants]
  );

  /** Prefer per-currency breakdown so mixed package currencies never blend totals. */
  const periodTotal = (valueKey) => {
    const byCurrency = payRun?.totals_by_currency || [];
    if (byCurrency.length > 1) {
      return byCurrency
        .map((row) => money(row[valueKey] || 0, row.currency))
        .join(' · ');
    }
    if (byCurrency.length === 1) {
      return money(payRun?.totals?.[valueKey] || byCurrency[0][valueKey] || 0, byCurrency[0].currency);
    }
    if (payRunCurrencies.length > 1) return 'Mixed currencies';
    return money(payRun?.totals?.[valueKey] || 0, payRunCurrencies[0]);
  };

  const shiftPeriod = (delta) => {
    if (!periodEndYmd) return;
    api
      .get('/services/finance/pay-run', {
        params: {
          as_of: periodEndYmd,
          shift: delta,
          q: searchDebounced || undefined,
        },
      })
      .then((r) => {
        const next = r.data?.period;
        if (next) {
          setPeriodStart(toYmd(next.start));
          setPeriodEnd(toYmd(next.end));
        }
      })
      .catch((e) => void appAlert(e.response?.data?.error || e.message));
  };

  const toggleConsultantSelect = (id) => {
    setSelectedConsultantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const invoiceSelectedConsultants = async () => {
    const targets = consultants.filter(
      (c) =>
        selectedConsultantIds.includes(c.consultant_id) &&
        (c.invoice_status === 'ready' || c.invoice_status === 'partial')
    );
    if (!targets.length) {
      void appAlert('Select consultants with uninvoiced payouts in this pay period.');
      return;
    }
    if (
      !await appConfirm(
        `Generate ${targets.length} invoice(s) for the pay period ${periodStartYmd} → ${periodEndYmd}? Only uninvoiced payouts in this cycle are included.`
      )
    ) {
      return;
    }

    const groups = [];
    for (const c of targets) {
      const label = `${c.first_name || ''} ${c.last_name || ''}`.trim() || `#${c.consultant_id}`;
      try {
        const rows = await api
          .get('/services/finance/ledger', {
            params: {
              period_start: periodStartYmd,
              period_end: periodEndYmd,
              consultant_id: c.consultant_id,
              payouts: 1,
              uninvoiced: 1,
              limit: 500,
            },
          })
          .then((r) => r.data);
        if (rows.length) {
          groups.push({ entry_ids: rows.map((r) => r.id), label });
        }
      } catch (e) {
        void appAlert(
          `Could not load payouts for ${label}: ${
            e.response?.data?.error || e.message
          }. No invoices were generated.`
        );
        return;
      }
    }
    if (!groups.length) {
      void appAlert('No uninvoiced payouts found for the selection in this period.');
      return;
    }
    generateInvoiceMutation.mutate({ consultantGroups: groups });
  };

  const invoiceConsultantPeriod = async () => {
    const ids =
      selectedEntryIds.length > 0
        ? selectedEntryIds
        : uninvoicedEntries.map((r) => r.id);
    if (!ids.length) {
      void appAlert('No uninvoiced payouts in this pay period for the consultant.');
      return;
    }
    if (
      !await appConfirm(
        `Generate one invoice for ${selectedConsultant?.first_name || ''} ${
          selectedConsultant?.last_name || ''
        } covering ${ids.length} payout(s) in ${periodStartYmd} → ${periodEndYmd}?`
      )
    ) {
      return;
    }
    generateInvoiceMutation.mutate({ entryIds: ids });
  };

  return (
    <div className="svc-finance">
      <div className="svc-tabs svc-finance-tabs" role="tablist" aria-label="Consultant payouts sections">
        <button
          type="button"
          role="tab"
          aria-selected={workspaceTab === 'pay-run'}
          className={`svc-tab ${workspaceTab === 'pay-run' ? 'is-active' : ''}`}
          onClick={() => selectWorkspaceTab('pay-run')}
        >
          <FiClock aria-hidden /> Pay run
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workspaceTab === 'invoices'}
          className={`svc-tab ${workspaceTab === 'invoices' ? 'is-active' : ''}`}
          onClick={() => selectWorkspaceTab('invoices')}
        >
          <FiFileText aria-hidden /> Payout invoices
        </button>
      </div>

      {workspaceTab === 'invoices' ? (
        <section className="svc-panel svc-payout-invoices">
          <div className="svc-panel-head">
            <div>
              <h2>Payout invoices</h2>
              <p className="svc-panel-meta">
                Historical PAY-* documents across all pay periods. Customer INV-* invoices stay under
                Sales.
              </p>
            </div>
          </div>
          <div className="list-filters-panel list-toolbar-panel svc-payout-invoice-toolbar">
            <div className="filter-row list-toolbar-row">
              <div className="filter-group filter-group--wide list-toolbar-search">
                <ListSearchBar
                  id="payout-invoice-search"
                  value={invoiceSearch}
                  onChange={setInvoiceSearch}
                  placeholder="Search PAY number or consultant…"
                  label="Search payout invoices"
                />
              </div>
            </div>
          </div>
          {loadingPayoutInvoices ? (
            <p className="svc-empty">Loading invoices…</p>
          ) : payoutInvoices.length === 0 ? (
            <div className="svc-empty-inline">
              <FiFileText aria-hidden />
              <p>
                {invoiceSearchDebounced
                  ? 'No payout invoices match your search.'
                  : 'No payout invoices yet. Generate one from the Pay run tab.'}
              </p>
            </div>
          ) : (
            <div className="svc-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">Consultant</th>
                    <th scope="col">Period</th>
                    <th scope="col">Issued</th>
                    <th scope="col">Entries</th>
                    <th scope="col" className="money-cell">
                      Total
                    </th>
                    <th scope="col">Status</th>
                    <th scope="col">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPayoutInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        <span className="svc-doc-number">{inv.number}</span>
                      </td>
                      <td>{inv.client_name || '—'}</td>
                      <td>
                        {inv.ledger_period_start && inv.ledger_period_end
                          ? `${formatDateOnly(inv.ledger_period_start)} → ${formatDateOnly(
                              inv.ledger_period_end
                            )}`
                          : '—'}
                      </td>
                      <td>{inv.issued_at ? formatDateOnly(inv.issued_at) : formatDateOnly(inv.createdon_timestamp)}</td>
                      <td>{inv.ledger_entry_count ?? '—'}</td>
                      <td className="money-cell">{money(inv.total, inv.currency)}</td>
                      <td>
                        <StatusBadge status={inv.status} label={uiStatusLabel(inv.status)} />
                      </td>
                      <td>
                        {inv.pdf_r2_key ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => downloadPayoutInvoicePdf(inv.id, inv.number)}
                          >
                            <FiDownload aria-hidden /> Download
                          </button>
                        ) : (
                          <span className="svc-panel-meta">No PDF</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {payoutInvoices.length > 0 ? (
            <ListPager
              page={invoicePagination.page}
              pageCount={invoicePagination.pageCount}
              total={invoicePagination.total}
              limit={invoicePagination.limit}
              onPageChange={setInvoicePage}
            />
          ) : null}
        </section>
      ) : (
        <>
      <section className="svc-panel svc-paycycle-bar">
        <div className="svc-paycycle-main">
          <div>
            <p className="svc-panel-meta">Pay cycle</p>
            <h2>
              {PAY_CYCLE_LABEL(settingsData?.config?.cycle_type)}
              {periodReady ? (
                <span className="svc-paycycle-dates">
                  {' '}
                  {formatDateOnly(periodStart)} → {formatDateOnly(periodEnd)}
                </span>
              ) : null}
            </h2>
            <p className="svc-panel-meta">
              Consultant payout documents (PAY-*) only — operational earnings statements, not
              customer revenue or AR. Customer invoices (INV-*) are created from Client POs and
              Quotes &amp; invoices. Payroll remittance and tax filing stay in external systems.
            </p>
          </div>
          <div className="svc-paycycle-nav">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => shiftPeriod(-1)}
              disabled={!periodReady}
              aria-label="Previous pay period"
            >
              <FiChevronLeft />
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const current = settingsData?.current_period;
                if (current) {
                  setPeriodStart(current.start);
                  setPeriodEnd(current.end);
                }
              }}
              disabled={!settingsData?.current_period}
            >
              Current
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => shiftPeriod(1)}
              disabled={!periodReady}
              aria-label="Next pay period"
            >
              <FiChevronRight />
            </button>
            {canManage && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowSettings(true)}
              >
                <FiSettings /> Cycle settings
              </button>
            )}
          </div>
        </div>
        {(settingsData?.recent_periods || []).length > 0 && (
          <div className="svc-paycycle-periods">
            <label>
              Jump to period
              <select
                value={`${periodStart}|${periodEnd}`}
                onChange={(e) => {
                  const [start, end] = e.target.value.split('|');
                  setPeriodStart(start);
                  setPeriodEnd(end);
                }}
              >
                {(settingsData.recent_periods || []).map((p) => (
                  <option key={`${p.start}-${p.end}`} value={`${p.start}|${p.end}`}>
                    {p.label || `${p.start} → ${p.end}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </section>

      {canManage ? (
        <section className="svc-panel" style={{ marginTop: 12 }}>
          <div className="svc-panel-head">
            <div>
              <h3 style={{ margin: 0 }}>Tax filing handoff (external systems)</h3>
              <p className="svc-panel-meta">
                Download CSV worksheets for your payroll / tax-filing tools. Godash does not file
                1099, T4A, or TDS returns.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="finance-tax-export-year">Tax year</label>
                <select
                  id="finance-tax-export-year"
                  value={exportYear}
                  onChange={(e) => setExportYear(Number(e.target.value))}
                >
                  {Array.from(
                    { length: TAX_EXPORT_YEAR_SPAN },
                    (_, i) => defaultTaxExportYear() + 1 - i
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              {['1099', 't4a', 'tds'].map((format) => (
                <button
                  key={format}
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={async () => {
                    try {
                      await downloadAuthenticatedFile(
                        `/services/finance/tax-exports?format=${format}&year=${exportYear}`,
                        `${format}-handoff-${exportYear}.csv`
                      );
                    } catch (e) {
                      void appAlert(e.response?.data?.error || e.message);
                    }
                  }}
                >
                  Export {format.toUpperCase()} CSV
                </button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <div className="svc-stat-grid">
        <div className="svc-stat-card svc-stat-card--money">
          <FiDollarSign className="svc-stat-icon" aria-hidden />
          <div>
            <span className="svc-stat-value">
              {periodTotal('uninvoiced_payouts')}
            </span>
            <span className="svc-stat-label">Uninvoiced this period</span>
          </div>
        </div>
        <div className="svc-stat-card">
          <FiClock className="svc-stat-icon" aria-hidden />
          <div>
            <span className="svc-stat-value">
              {periodTotal('hourly_payouts')}
            </span>
            <span className="svc-stat-label">Hourly in period</span>
          </div>
        </div>
        <div className="svc-stat-card">
          <FiCheck className="svc-stat-icon" aria-hidden />
          <div>
            <span className="svc-stat-value">{periodTotal('hire_bonuses')}</span>
            <span className="svc-stat-label">Bonuses in period</span>
          </div>
        </div>
        <div className="svc-stat-card svc-stat-card--pending">
          <FiFileText className="svc-stat-icon" aria-hidden />
          <div>
            <span className="svc-stat-value">{payRun?.ready_count || 0}</span>
            <span className="svc-stat-label">Consultants ready</span>
          </div>
        </div>
      </div>

      <div className="svc-payrun-layout">
        <section className="svc-panel svc-payrun-list">
          <div className="svc-panel-head">
            <div>
              <h2>Consultants</h2>
              <p className="svc-panel-meta">
                {filteredConsultants.length} shown
                {payRun?.consultant_count != null
                  ? ` · ${payRun.consultant_count} with activity`
                  : ''}
              </p>
            </div>
            {canManage && (
              <LoadingButton
                type="button"
                className="btn btn-primary btn-sm"
                icon={FiFileText}
                loading={generateInvoiceMutation.isLoading}
                disabled={
                  selectedConsultantIds.length === 0 || generateInvoiceMutation.isLoading
                }
                loadingLabel="Invoicing…"
                onClick={invoiceSelectedConsultants}
              >
                Invoice selected
              </LoadingButton>
            )}
          </div>

          <div className="svc-payrun-filters">
            <label className="svc-payrun-search">
              <FiSearch aria-hidden />
              <input
                type="search"
                placeholder="Search consultants…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by invoice status"
            >
              <option value="payable">Payable (ready / partial)</option>
              <option value="ready">Ready only</option>
              <option value="partial">Partial only</option>
              <option value="invoiced">Invoiced</option>
              <option value="all">All with activity</option>
            </select>
          </div>

          {loadingSettings || loadingPayRun ? (
            <p className="svc-empty">Loading pay run…</p>
          ) : pageRows.length === 0 ? (
            <p className="svc-empty">No consultants match this pay period and filters.</p>
          ) : (
            <div className="svc-table-scroll">
              <table className="data-table svc-payrun-table">
                <thead>
                  <tr>
                    {canManage && <th className="svc-ledger-check" />}
                    <th>Consultant</th>
                    <th>Hours</th>
                    <th>Due</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => {
                    const active =
                      String(selectedConsultantId) === String(row.consultant_id);
                    const payable =
                      row.invoice_status === 'ready' || row.invoice_status === 'partial';
                    return (
                      <tr
                        key={row.consultant_id}
                        className={active ? 'is-selected' : ''}
                        onClick={() => {
                          setSelectedConsultantId(row.consultant_id);
                          setSelectedEntryIds([]);
                        }}
                      >
                        {canManage && (
                          <td
                            className="svc-ledger-check"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {payable ? (
                              <input
                                type="checkbox"
                                checked={selectedConsultantIds.includes(row.consultant_id)}
                                onChange={() => toggleConsultantSelect(row.consultant_id)}
                                aria-label={`Select ${row.first_name} ${row.last_name}`}
                              />
                            ) : null}
                          </td>
                        )}
                        <td>
                          <strong>
                            {row.first_name} {row.last_name}
                          </strong>
                          <div className="svc-panel-meta">
                            {row.entry_count} entr{Number(row.entry_count) === 1 ? 'y' : 'ies'}
                          </div>
                        </td>
                        <td className="numeric-cell">{Number(row.hours_total || 0).toFixed(2)}</td>
                        <td className="money-cell">{money(row.uninvoiced_total, row.currency)}</td>
                        <td>
                          <span
                            className={`svc-invoice-pill svc-invoice-pill--${row.invoice_status}`}
                          >
                            {statusLabel(row.invoice_status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pageCount > 1 && (
            <div className="svc-payrun-pager">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </button>
              <span className="svc-panel-meta">
                Page {page + 1} / {pageCount}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </section>

        <section className="svc-panel svc-payrun-detail">
          {!selectedConsultantId ? (
            <div className="svc-empty-inline">
              <FiDollarSign aria-hidden />
              <p>Select a consultant to review period payouts and generate their invoice.</p>
            </div>
          ) : (
            <>
              <div className="svc-panel-head">
                <div>
                  <h2>
                    {selectedConsultant?.first_name} {selectedConsultant?.last_name}
                  </h2>
                  <p className="svc-panel-meta">
                    {money(selectedConsultant?.uninvoiced_total || 0, selectedConsultant?.currency)}{' '}
                    uninvoiced ·{' '}
                    {money(selectedConsultant?.payout_total || 0, selectedConsultant?.currency)}{' '}
                    period total
                  </p>
                </div>
                {canManage && uninvoicedEntries.length > 0 && (
                  <LoadingButton
                    type="button"
                    className="btn btn-primary btn-sm"
                    icon={FiFileText}
                    loading={generateInvoiceMutation.isLoading}
                    disabled={generateInvoiceMutation.isLoading}
                    loadingLabel="Generating…"
                    onClick={invoiceConsultantPeriod}
                  >
                    {selectedEntryIds.length > 0
                      ? `Invoice ${selectedEntryIds.length} selected`
                      : `Invoice period (${uninvoicedEntries.length})`}
                  </LoadingButton>
                )}
              </div>

              {canManage && selectedConsultantId ? (
                <PayeeTaxProfileEditor
                  key={selectedConsultantId}
                  userId={selectedConsultantId}
                />
              ) : null}

              {loadingEntries ? (
                <p className="svc-empty">Loading entries…</p>
              ) : consultantEntries.length === 0 ? (
                <p className="svc-empty">No payout entries in this pay period.</p>
              ) : (
                <div className="svc-table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {canManage && <th className="svc-ledger-check" />}
                        <th>Date</th>
                        <th>Type</th>
                        <th>Activity</th>
                        <th>Amount</th>
                        <th>Invoice</th>
                        {canManage && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {consultantEntries.map((row) => {
                        const invoiced = Boolean(row.sales_document_id);
                        return (
                          <tr key={row.id}>
                            {canManage && (
                              <td className="svc-ledger-check">
                                {!invoiced ? (
                                  <input
                                    type="checkbox"
                                    checked={selectedEntryIds.includes(row.id)}
                                    onChange={() =>
                                      setSelectedEntryIds((prev) =>
                                        prev.includes(row.id)
                                          ? prev.filter((x) => x !== row.id)
                                          : [...prev, row.id]
                                      )
                                    }
                                    aria-label={`Select ledger entry ${row.id}`}
                                  />
                                ) : null}
                              </td>
                            )}
                            <td>
                              {row.entry_date || row.reference_date
                                ? formatDateOnly(row.entry_date || row.reference_date)
                                : '—'}
                            </td>
                            <td>{row.entry_type}</td>
                            <td>
                              {row.activity_description || row.activity_type || row.description || '—'}
                            </td>
                            <td className="money-cell">{money(row.amount, row.currency)}</td>
                            <td>
                              {invoiced ? (
                                <button
                                  type="button"
                                  className="btn-link svc-invoice-link"
                                  onClick={() =>
                                    downloadPayoutInvoicePdf(
                                      row.sales_document_id,
                                      row.sales_document_number
                                    )
                                  }
                                  title="Download payout invoice PDF"
                                >
                                  {row.sales_document_number || `#${row.sales_document_id}`}
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                            {canManage && (
                              <td>
                                {row.status === 'pending' && (
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() =>
                                      financeUpdateMutation.mutate({
                                        id: row.id,
                                        status: 'approved',
                                      })
                                    }
                                  >
                                    <FiCheck /> Approve
                                  </button>
                                )}
                                {row.status === 'approved' && (
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() =>
                                      financeUpdateMutation.mutate({
                                        id: row.id,
                                        status: 'paid',
                                      })
                                    }
                                  >
                                    Mark paid
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>
        </>
      )}

      <Modal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        ariaLabel="Pay cycle settings"
      >
        <h2>Pay cycle settings</h2>
        <p className="svc-form-hint">
          Choose how consultant payout periods are calculated. Invoices can only include payouts
          dated inside the selected period.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveSettingsMutation.mutate(settingsForm);
          }}
        >
          <div className="form-group">
            <label htmlFor="finance-cycle-type">Cycle</label>
            <select
              id="finance-cycle-type"
              value={settingsForm.cycle_type}
              onChange={(e) =>
                setSettingsForm({ ...settingsForm, cycle_type: e.target.value })
              }
            >
              {(cycleTypes.length
                ? cycleTypes
                : [
                    { id: 'days_28', label: 'Every 28 days' },
                    { id: 'days_25', label: 'Every 25 days' },
                    { id: 'biweekly_friday', label: 'Bi-weekly (pay Friday)' },
                  ]
              ).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="finance-cycle-anchor">
              {settingsForm.cycle_type === 'biweekly_friday'
                ? 'Anchor pay Friday'
                : 'Cycle start date'}
            </label>
            <input
              id="finance-cycle-anchor"
              type="date"
              value={settingsForm.anchor_date || ''}
              onChange={(e) =>
                setSettingsForm({ ...settingsForm, anchor_date: e.target.value })
              }
              required
            />
            <p className="svc-form-hint">
              {settingsForm.cycle_type === 'biweekly_friday'
                ? 'Pick any known pay Friday; the bi-weekly cadence aligns to it.'
                : 'Periods of 25 or 28 days count forward from this date.'}
            </p>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSettings(false)}
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={saveSettingsMutation.isLoading}
            >
              Save cycle
            </LoadingButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function emptyPayeeTaxProfile() {
  return {
    country_code: 'US',
    worker_type: 'contractor',
    tin_type: 'ein',
    tin_value: '',
    legal_name: '',
    address: '',
    state_province: '',
    w9_received_at: '',
    tds_section: '194J',
  };
}

function PayeeTaxProfileEditor({ userId }) {
  const [form, setForm] = useState(emptyPayeeTaxProfile);
  const fieldId = (name) => `payee-tax-${userId}-${name}`;
  const { data, isLoading, isError, error } = useQuery(
    ['payee-tax-profile', userId],
    () => api.get(`/services/finance/payee-tax-profiles/${userId}`).then((r) => r.data),
    { enabled: Boolean(userId) }
  );

  // Always reset — a consultant without a profile must clear the previous TIN
  // rather than inherit it and save it onto the wrong person.
  useEffect(() => {
    if (!data) return;
    const profile = data.profile;
    if (!profile) {
      setForm(emptyPayeeTaxProfile());
      return;
    }
    setForm({
      country_code: profile.country_code || 'US',
      worker_type: profile.worker_type || 'contractor',
      tin_type: profile.tin_type || 'ein',
      tin_value: profile.tin_value || '',
      legal_name: profile.legal_name || '',
      address: profile.address || '',
      state_province: profile.state_province || '',
      w9_received_at: profile.w9_received_at || '',
      tds_section: profile.tds_section || '194J',
    });
  }, [data]);

  const saveMutation = useMutation(
    () => api.put(`/services/finance/payee-tax-profiles/${userId}`, form),
    {
      onSuccess: () => void appAlert('Payee tax profile saved for PAY documents and CSV handoff.'),
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  if (isError) {
    return (
      <p className="svc-panel-meta">
        Could not load payee tax profile: {error?.response?.data?.error || error?.message}
      </p>
    );
  }

  if (isLoading || !data) {
    return <p className="svc-panel-meta">Loading payee tax profile…</p>;
  }

  return (
    <div className="svc-form-section" style={{ marginBottom: 16 }}>
      <p className="svc-panel-meta" style={{ marginBottom: 8 }}>
        Payee tax profile (printed on PAY docs; exported to external tax systems)
      </p>
      <div className="svc-form-grid svc-form-grid--3">
        <div className="form-group">
          <label htmlFor={fieldId('country')}>Country</label>
          <select
            id={fieldId('country')}
            value={form.country_code}
            onChange={(e) => setForm({ ...form, country_code: e.target.value })}
          >
            <option value="US">US</option>
            <option value="CA">CA</option>
            <option value="IN">IN</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={fieldId('tin-type')}>TIN type</label>
          <select
            id={fieldId('tin-type')}
            value={form.tin_type}
            onChange={(e) => setForm({ ...form, tin_type: e.target.value })}
          >
            {TAX_ID_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={fieldId('tin-value')}>TIN / PAN</label>
          <input
            id={fieldId('tin-value')}
            value={form.tin_value}
            onChange={(e) => setForm({ ...form, tin_value: e.target.value })}
          />
        </div>
      </div>
      <div className="svc-form-grid svc-form-grid--2">
        <div className="form-group">
          <label htmlFor={fieldId('legal-name')}>Legal name</label>
          <input
            id={fieldId('legal-name')}
            value={form.legal_name}
            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label htmlFor={fieldId('w9')}>W-9 received</label>
          <input
            id={fieldId('w9')}
            type="date"
            value={form.w9_received_at || ''}
            onChange={(e) => setForm({ ...form, w9_received_at: e.target.value })}
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor={fieldId('address')}>Address</label>
        <input
          id={fieldId('address')}
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
      <LoadingButton
        type="button"
        className="btn btn-secondary btn-sm"
        loading={saveMutation.isLoading}
        onClick={() => saveMutation.mutate()}
      >
        Save payee profile
      </LoadingButton>
    </div>
  );
}

function PAY_CYCLE_LABEL(type) {
  switch (type) {
    case 'days_25':
      return '25-day cycle';
    case 'biweekly_friday':
      return 'Bi-weekly Friday';
    case 'days_28':
    default:
      return '28-day cycle';
  }
}
